import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { alertService } from '../../../services/alertService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const LiveMetricsPanel = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);

  const projects = [
    { id: 'project-1', name: 'Restauración Forestal Amazonía' },
    { id: 'project-2', name: 'Conservación Bosque Nublado' }
  ];

  useEffect(() => {
    if (selectedProject) {
      fetchMetrics(selectedProject);
    }
  }, [selectedProject]);

  const fetchMetrics = async (projectId) => {
    try {
      setLoading(true);
      const data = await alertService?.getAlertMetrics(projectId);
      setMetrics(data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMetricStatus = (value, min, max) => {
    if (max && value > max) return 'critical';
    if (min && value < min) return 'warning';
    return 'normal';
  };

  const getStatusColor = (status) => {
    const colors = {
      critical: 'text-destructive bg-destructive/10 border-destructive',
      warning: 'text-warning bg-warning/10 border-warning',
      normal: 'text-success bg-success/10 border-success'
    };
    return colors?.[status] || colors?.normal;
  };

  const getStatusIcon = (status) => {
    const icons = {
      critical: 'AlertCircle',
      warning: 'AlertTriangle',
      normal: 'CheckCircle'
    };
    return icons?.[status] || 'CheckCircle';
  };

  // Group metrics by name for trend visualization
  const metricTrends = metrics?.reduce((acc, metric) => {
    if (!acc?.[metric?.metricName]) {
      acc[metric?.metricName] = [];
    }
    acc?.[metric?.metricName]?.push({
      timestamp: new Date(metric?.timestamp)?.toLocaleTimeString(),
      value: metric?.metricValue,
      threshold: metric?.thresholdMax || metric?.thresholdMin
    });
    return acc;
  }, {});

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground mb-1">Live Metrics Monitoring</h2>
        <p className="text-sm text-muted-foreground">Real-time project KPIs with threshold visualization and trend analysis</p>
      </div>

      {/* Project Selector */}
      <div className="mb-6">
        <label className="text-sm font-medium text-foreground mb-2 block">Select Project</label>
        <div className="flex gap-2">
          {projects?.map(project => (
            <button
              key={project?.id}
              onClick={() => setSelectedProject(project?.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                selectedProject === project?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              {project?.name}
            </button>
          ))}
        </div>
      </div>

      {!selectedProject ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Select a Project</h3>
          <p className="text-sm text-muted-foreground">Choose a project to view live metrics and threshold monitoring</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading metrics...</p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Current Metrics Grid */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Current Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {metrics?.slice(0, 6)?.map(metric => {
                const status = getMetricStatus(metric?.metricValue, metric?.thresholdMin, metric?.thresholdMax);
                return (
                  <div key={metric?.id} className={`bg-card border-2 rounded-lg p-4 ${getStatusColor(status)}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">{metric?.metricName}</p>
                        <p className="text-2xl font-bold text-foreground">
                          {metric?.metricValue?.toLocaleString()}
                          {metric?.unit && <span className="text-sm font-normal text-muted-foreground ml-1">{metric?.unit}</span>}
                        </p>
                      </div>
                      <Icon name={getStatusIcon(status)} size={20} className={status === 'critical' ? 'text-destructive' : status === 'warning' ? 'text-warning' : 'text-success'} />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Threshold:</span>
                      <span className="font-medium text-foreground">
                        {metric?.thresholdMin && `${metric?.thresholdMin} - `}
                        {metric?.thresholdMax}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Trend Visualization */}
          {Object.keys(metricTrends)?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">Metric Trends</h3>
              <div className="space-y-6">
                {Object.entries(metricTrends)?.slice(0, 3)?.map(([metricName, data]) => (
                  <div key={metricName} className="bg-card border border-border rounded-lg p-4">
                    <h4 className="text-sm font-medium text-foreground mb-4">{metricName}</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={data?.slice(-10)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                        <XAxis dataKey="timestamp" stroke="var(--muted-foreground)" style={{ fontSize: '12px' }} />
                        <YAxis stroke="var(--muted-foreground)" style={{ fontSize: '12px' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'var(--card)',
                            border: '1px solid var(--border)',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="value" stroke="var(--primary)" strokeWidth={2} name="Current Value" />
                        <Line type="monotone" dataKey="threshold" stroke="var(--destructive)" strokeWidth={2} strokeDasharray="5 5" name="Threshold" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Threshold Violations Summary */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Threshold Status Summary</h3>
            <div className="space-y-2">
              {metrics?.map(metric => {
                const status = getMetricStatus(metric?.metricValue, metric?.thresholdMin, metric?.thresholdMax);
                if (status === 'normal') return null;
                return (
                  <div key={metric?.id} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon name={getStatusIcon(status)} size={16} className={status === 'critical' ? 'text-destructive' : 'text-warning'} />
                      <div>
                        <p className="text-sm font-medium text-foreground">{metric?.metricName}</p>
                        <p className="text-xs text-muted-foreground">
                          {status === 'critical' ? 'Exceeds maximum threshold' : 'Below minimum threshold'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-foreground">{metric?.metricValue?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">vs {metric?.thresholdMax || metric?.thresholdMin}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveMetricsPanel;